import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Slider,
    Switch,
    TextField,
    Typography,
} from "@mui/material"
import { FeatureFlag } from "../../../../services/feature-flag.service"

type Props = {
    open: boolean
    flag?: FeatureFlag | null
    onClose: () => void
    onSubmit: (input: {
        name: string
        enabled: boolean
        rolloutPercentage: number
        description: string | null
    }) => void
}

export const FeatureFlagFormDialog: React.FC<Props> = ({ open, flag, onClose, onSubmit }) => {
    const isEdit = !!flag

    const [name, setName] = useState("")
    const [enabled, setEnabled] = useState(false)
    const [rollout, setRollout] = useState(0)
    const [description, setDescription] = useState("")
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (open) {
            setName(flag?.name ?? "")
            setEnabled(flag?.enabled ?? false)
            setRollout(flag?.rollout_percentage ?? 0)
            setDescription(flag?.description ?? "")
            setError(null)
        }
    }, [open, flag])

    const handleSubmit = () => {
        if (!isEdit) {
            if (!name.trim()) {
                setError("Nome é obrigatório")
                return
            }
            if (!/^[a-z0-9_]+$/.test(name)) {
                setError("Use apenas letras minúsculas, números e underscore")
                return
            }
        }
        onSubmit({
            name: name.trim(),
            enabled,
            rolloutPercentage: rollout,
            description: description.trim() || null,
        })
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEdit ? `Editar: ${flag?.name}` : "Nova feature flag"}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {!isEdit && (
                        <TextField
                            label="Nome da flag"
                            placeholder="ex: use_new_pdf_version"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!error}
                            helperText={error || "Use snake_case. Não pode ser alterado depois."}
                            fullWidth
                        />
                    )}

                    <TextField
                        label="Descrição"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={(_, value) => setEnabled(value)}
                            />
                        }
                        label="Ligada globalmente (todos os usuários veem)"
                    />

                    <Box>
                        <Typography gutterBottom>
                            Rollout: <strong>{rollout}%</strong> dos usuários (quando não está ligada globalmente)
                        </Typography>
                        <Slider
                            value={rollout}
                            onChange={(_, value) => setRollout(value as number)}
                            min={0}
                            max={100}
                            step={5}
                            marks={[
                                { value: 0, label: "0%" },
                                { value: 50, label: "50%" },
                                { value: 100, label: "100%" },
                            ]}
                            valueLabelDisplay="auto"
                            disabled={enabled}
                        />
                        <Typography variant="caption" color="text.secondary">
                            A escolha é determinística: o mesmo usuário sempre cai no mesmo grupo.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleSubmit}>
                    {isEdit ? "Salvar" : "Criar"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}